import React from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import {
  TrendingUp as MomentumIcon,
  Timeline as TrendIcon,
  BarChart as SupportResistanceIcon,
} from '@mui/icons-material';

const SIGNAL_TYPES = [
  {
    id: 'momentum',
    label: 'Momentum Reversals',
    icon: MomentumIcon,
    description: 'DSS divergences, overbought/oversold conditions',
    color: 'var(--dwlf-accent)',
  },
  {
    id: 'trend',
    label: 'Trend Following',
    icon: TrendIcon,
    description: 'EMA alignment, bullish/bearish setups',
    color: 'var(--dwlf-up)',
  },
  {
    id: 'sr',
    label: 'S/R Touches',
    icon: SupportResistanceIcon,
    description: 'Price at key support or resistance levels',
    color: 'var(--dwlf-warn)',
  },
];

const SignalPreferencesStep = ({ selectedSignals, onSelectSignals }) => {
  const toggleSignal = (signalId) => {
    if (selectedSignals.includes(signalId)) {
      onSelectSignals(selectedSignals.filter((s) => s !== signalId));
    } else {
      onSelectSignals([...selectedSignals, signalId]);
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontFamily: '"Space Grotesk", sans-serif',
          fontWeight: 600,
          mb: 1,
        }}
      >
        What signals interest you?
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'var(--dwlf-ink-60)',
          mb: 4,
          fontFamily: '"IBM Plex Mono", monospace',
        }}
      >
        Choose signal types to monitor. We'll notify you when they fire.
      </Typography>

      <Grid container spacing={2}>
        {SIGNAL_TYPES.map((signal) => {
          const Icon = signal.icon;
          const isSelected = selectedSignals.includes(signal.id);

          return (
            <Grid size={{ xs: 12 }} key={signal.id}>
              <Paper
                onClick={() => toggleSignal(signal.id)}
                sx={{
                  p: 3,
                  cursor: 'pointer',
                  bgcolor: isSelected
                    ? `color-mix(in srgb, ${signal.color} 10%, transparent)`
                    : 'var(--dwlf-elevated)',
                  border: isSelected
                    ? `2px solid ${signal.color}`
                    : '2px solid transparent',
                  outline: isSelected
                    ? 'none'
                    : '1px solid var(--dwlf-ink-08)',
                  borderRadius: 0,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: isSelected
                      ? `color-mix(in srgb, ${signal.color} 15%, transparent)`
                      : 'var(--dwlf-ink-05)',
                    borderColor: signal.color,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Icon
                    sx={{
                      fontSize: 40,
                      color: isSelected ? signal.color : 'var(--dwlf-ink-50)',
                      mr: 3,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: '"Space Grotesk", sans-serif',
                        fontWeight: 600,
                        color: isSelected ? signal.color : 'var(--dwlf-text)',
                        mb: 0.5,
                      }}
                    >
                      {signal.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'var(--dwlf-ink-60)',
                        fontFamily: '"IBM Plex Mono", monospace',
                        fontSize: '0.85rem',
                      }}
                    >
                      {signal.description}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default SignalPreferencesStep;
